import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  buildDevicePassport,
  computeTrustScore,
  cosmeticConditionScore,
  cosmeticGradeFromCondition,
  declarationHighlights,
  hasBatteryTransparency,
  isPlatformVerifiedListing,
  formatApproxMarketEur,
  priceInsight,
  sellerDeclarationScore100,
  technicalConditionScore,
  trustChecklistForDisplay,
  type ListingTrustInput,
} from '@redevicer/shared'

interface Props {
  listing: ListingTrustInput
  loggedIn?: boolean
  marketEurOverride?: number | null
  onRequireLogin?: () => void
}

export default function TrustReportPanel({
  listing,
  loggedIn = false,
  marketEurOverride,
  onRequireLogin,
}: Props) {
  const { t } = useTranslation()
  const score = useMemo(() => computeTrustScore(listing), [listing])
  const insight = useMemo(
    () =>
      priceInsight({
        price: listing.price,
        ai_recommended_eur: marketEurOverride ?? listing.ai_recommended_eur,
      }),
    [listing.price, listing.ai_recommended_eur, marketEurOverride],
  )
  const passport = useMemo(() => buildDevicePassport(listing), [listing])
  const checks = useMemo(() => trustChecklistForDisplay(listing), [listing])
  const grade = cosmeticGradeFromCondition(listing.condition)
  const platformVerified = isPlatformVerifiedListing(listing)
  const locked = !loggedIn
  const showBatteryHighlight = listing.battery_health != null && !hasBatteryTransparency(listing)
  const peerHighlights = useMemo(() => declarationHighlights(listing), [listing])
  const declarationScore = useMemo(() => sellerDeclarationScore100(listing), [listing])

  const reportContent = (
    <>
      <div className="trust-report-head">
        <div>
          <p className="trust-report-kicker">
            {platformVerified ? t('trust.report.verifiedBadge') : t('trust.report.declaredBadge')}
          </p>
          <h3 id="trust-report-heading" className="trust-report-title">
            {t('trust.report.title')}
          </h3>
          <p className="trust-report-lead">{t('trust.report.lead')}</p>
        </div>
        <div className="trust-score-pill" aria-label={t('trust.report.scoreAria', { score })}>
          <span className="trust-score-star" aria-hidden="true">
            ★
          </span>
          <span className="trust-score-value">{score}</span>
          <span className="trust-score-max">/100</span>
        </div>
      </div>

      {platformVerified || showBatteryHighlight || listing.imei_verified || peerHighlights.length > 0 ? (
        <ul className="trust-highlights">
          {platformVerified ? (
            <li>✓ {t('trust.report.highlightInspection', { count: passport.diagnosticsTotal })}</li>
          ) : null}
          {peerHighlights.map((item) => (
            <li key={item.key}>
              ✓ {t(item.key, item.vars ?? {}) as string}
            </li>
          ))}
          {showBatteryHighlight ? (
            <li>✓ {t('trust.report.highlightBattery', { pct: listing.battery_health })}</li>
          ) : null}
          {listing.imei_verified ? <li>✓ {t('trust.report.highlightImei')}</li> : null}
          {platformVerified ? <li>✓ {t('trust.report.highlightWarranty')}</li> : null}
        </ul>
      ) : null}

      {insight.level !== 'unknown' && insight.marketEur != null ? (
        <div className={`trust-price-insight trust-price-insight--${insight.level}`}>
          <p>
            {t('trust.report.marketPrice')}: <strong>{formatApproxMarketEur(insight.marketEur)}</strong>
          </p>
          <p>
            {t('trust.report.redevicerPrice')}: <strong>€{listing.price.toFixed(2)}</strong>
          </p>
          {insight.level === 'great' && insight.savingsPct != null ? (
            <p className="trust-price-tag trust-price-tag--great">
              {t('trust.report.greatPrice', { pct: insight.savingsPct })}
            </p>
          ) : null}
          {insight.level === 'fair' ? (
            <p className="trust-price-tag trust-price-tag--fair">{t('trust.report.fairPrice')}</p>
          ) : null}
          {insight.level === 'above' ? (
            <p className="trust-price-tag trust-price-tag--above">{t('trust.report.abovePrice')}</p>
          ) : null}
        </div>
      ) : null}

      <div className="trust-grades">
        <div>
          <span className="trust-grade-label">{t('trust.report.technical')}</span>
          <strong>{technicalConditionScore(listing)}/10</strong>
        </div>
        <div>
          <span className="trust-grade-label">{t('trust.report.cosmetic')}</span>
          <strong>{cosmeticConditionScore(listing)}/10</strong>
        </div>
        <div>
          <span className="trust-grade-label">{t('trust.report.grade')}</span>
          <strong>{t(grade.labelKey)}</strong>
        </div>
        {!platformVerified ? (
          <div>
            <span className="trust-grade-label">{t('trust.report.declaration')}</span>
            <strong>{declarationScore}/100</strong>
          </div>
        ) : null}
      </div>

      <div className="trust-passport">
        <div className="trust-passport-head">
          <strong>{t('trust.passport.title')}</strong>
          <code>{passport.passportId}</code>
        </div>
        <dl className="trust-passport-grid">
          <div>
            <dt>{t('trust.passport.device')}</dt>
            <dd>{passport.deviceTitle}</dd>
          </div>
          {passport.inspectionDate ? (
            <div>
              <dt>{t('trust.passport.inspection')}</dt>
              <dd>{passport.inspectionDate}</dd>
            </div>
          ) : null}
          {passport.batteryPct != null ? (
            <div>
              <dt>{t('trust.passport.battery')}</dt>
              <dd>{passport.batteryPct}%</dd>
            </div>
          ) : null}
          <div>
            <dt>{t('trust.passport.diagnostics')}</dt>
            <dd>
              {passport.diagnosticsInspected
                ? `${passport.diagnosticsPassed}/${passport.diagnosticsTotal} ${t('trust.passport.passed')}`
                : t('trust.passport.notInspected')}
            </dd>
          </div>
          <div>
            <dt>{t('trust.passport.warranty')}</dt>
            <dd>{passport.warrantyLabel}</dd>
          </div>
        </dl>
        <p className="trust-passport-qr-hint">{t('trust.passport.qrHint')}</p>
      </div>

      <div className="trust-check-grid">
        {checks.map((c) => (
          <div key={c.id} className={`trust-check trust-check--${c.status}`}>
            <span className="trust-check-label">{t(c.labelKey)}</span>
            <span className="trust-check-value">{t(c.valueKey, c.valueVars ?? {})}</span>
          </div>
        ))}
      </div>

      {!platformVerified ? (
        <p className="trust-report-disclaimer">{t('trust.report.peerDisclaimer')}</p>
      ) : null}
    </>
  )

  return (
    <section
      className={`trust-report${locked ? ' trust-report--locked' : ''}`}
      aria-labelledby={locked ? undefined : 'trust-report-heading'}
      aria-label={locked ? t('trust.report.title') : undefined}
    >
      <div className="trust-report-inner">
        <div className="trust-report-content" aria-hidden={locked || undefined}>
          {reportContent}
        </div>
      </div>

      {locked ? (
        <div className="trust-report-paid-gate" role="status" aria-live="polite">
          <div className="trust-report-paid-gate__card">
            <span className="trust-report-paid-gate__icon" aria-hidden="true">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M7 10V8a5 5 0 0 1 10 0v2"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
                <rect x="5" y="10" width="14" height="11" rx="2.2" stroke="currentColor" strokeWidth="1.8" />
                <circle cx="12" cy="15.5" r="1.3" fill="currentColor" />
                <path d="M12 16.8v2.2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </span>
            <p className="trust-report-paid-gate__body">{t('trust.report.membersOnlyBody')}</p>
            {onRequireLogin ? (
              <button type="button" className="trust-report-paid-gate__cta" onClick={onRequireLogin}>
                {t('trust.report.membersOnlyCta')}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  )
}
